/**
 * AddUserModal
 *
 * Admin-only modal for pre-provisioning a stub user. Used inline from the
 * UserPicker (so you don't have to leave the page when assigning an AD that
 * isn't in the system yet) and from the admin Users page.
 *
 * Creates a row in the users table with no SSO link. The row goes straight to
 * status='active' (the admin is implicitly approving by creating it). When
 * that person later signs in via Google with the same email, the existing row
 * is reused (the auth callback matches by email and links the SSO id), so any
 * assignments already made stay attached to the same user id.
 *
 * The admin can optionally assign the new user to one or more groups at
 * creation time.
 */

import { useState, type FormEvent } from 'react';
import { X, Loader2, AlertCircle, UserPlus } from 'lucide-react';
import { useCreateStubUser, type UserLookupItem } from '@/hooks/useUserLookup';
import { useGroups } from '@/hooks/useGroups';

interface AddUserModalProps {
  /** Pre-fill the name field (e.g. from picker query text) */
  initialName?: string;
  /** Pre-fill the email field */
  initialEmail?: string;
  onClose: () => void;
  onCreated: (user: UserLookupItem) => void;
}

export function AddUserModal({
  initialName = '',
  initialEmail = '',
  onClose,
  onCreated,
}: AddUserModalProps) {
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const createUser = useCreateStubUser();
  const { data: groupsData } = useGroups();
  const availableGroups = groupsData?.groups || [];

  const toggleGroup = (groupId: string) => {
    const next = new Set(selectedGroupIds);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    setSelectedGroupIds(next);
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('A valid email is required');
      return;
    }

    try {
      const result = await createUser.mutateAsync({
        email: email.trim(),
        name: name.trim(),
        groupIds: Array.from(selectedGroupIds),
      });
      onCreated(result.user);
    } catch (e) {
      const err = e as { message?: string; status?: number };
      if (err.status === 409) {
        setError('A user with that email already exists. Use the picker to find them.');
      } else {
        setError(err.message || 'Failed to create user');
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-xtravirt-cyan" />
            <h2 className="font-heading text-lg font-semibold text-xtravirt-dark">Add new person</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-xs text-gray-500">
            Pre-provision someone before they sign in. They can be assigned as a lead, AD, or
            reviewer immediately. When they sign in via Google with the same email, the row links
            automatically.
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Full name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="e.g. Sarah Jones"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-xtravirt-cyan focus:border-xtravirt-cyan"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="sarah@xtravirt.com"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-xtravirt-cyan focus:border-xtravirt-cyan"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Must match the Google account they will sign in with.
            </p>
          </div>

          {availableGroups.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Groups{' '}
                <span className="text-gray-400 font-normal">
                  — optional, determines their permissions
                </span>
              </label>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-1">
                {availableGroups.map((g) => (
                  <label
                    key={g.id}
                    className="flex items-center gap-2 text-xs cursor-pointer py-1 px-2 hover:bg-white rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroupIds.has(g.id)}
                      onChange={() => toggleGroup(g.id)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-xtravirt-cyan"
                    />
                    <span className="text-gray-700 flex-1">{g.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary btn-sm"
              disabled={createUser.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary btn-sm flex items-center gap-1.5"
              disabled={createUser.isPending}
            >
              {createUser.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <UserPlus className="h-3.5 w-3.5" />
                  Add user
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
